import { isObservable, toJS } from 'mobx';
import {
  ExecutorActionError,
  ExecutorActionEvent,
  ExecutorActionFinished,
  ExecutorActionInfo,
  ExecutorEvents,
  ExecutorNewWorksheetCreated,
} from 'shared/execution/executors/events';
import { appEventBus } from 'shared/infrastructure/app-event-bus';
import { Flowstep, isNewFlowstepFormat, MigrationFlowstep, Operation } from 'shared/types/flowstep';
import { CancelError } from 'shared/utils/cancelToken';
import { ErrorReaction, NoDataError, ResultDataError } from 'shared/utils/flowstep';
import { Type } from 'src/components/base/MessagePane';
import { createExecutor, Data as V1FlowstepData } from 'src/services/flowstepExecution';
import office from 'src/services/office';
import { InstanceLogger } from 'src/services/runtime/operationsLog';
import { selectRange } from 'src/services/runtime/runnable';
import { contextStore, dataStore, flowstepStorage, requestStore, userStore } from 'src/stores';
import { Button } from 'src/v2/components/base/MessagePane';
import executorFactory from './executor-factory';
import { ConstantsConversionsError, PushError } from 'shared/execution/executors/common';

export enum StopReason {
  Normal = 'normal',
  ErrorReaction = 'error-reaction',
  User = 'user',
}

export const Buttons = {
  Stop: { key: 'stop', text: 'No' },
  Ignore: { key: 'ignore', text: 'Yes', primary: true },
  Cancel: { key: 'cancel', text: 'Cancel' },
  Create: { key: 'create', text: 'Yes', primary: true },
};

export const Data = {
  buttons: [Buttons.Stop, Buttons.Ignore],
  flowstepButtons: [Buttons.Cancel, Buttons.Create],
  maxErrorsToDisplay: 10,
  messagePaneTimeout: 10 * 1000, // in ms
  successText: 'Successfully executed',
  dataErrorsText: 'Errors were encountered',
};

const showPane = (params: Record<string, any>, logRange?: Excel.Range, errorsRange?: Excel.Range) => {
  errorsRange?.track();
  logRange?.track();

  const buttons: Button[] = [];
  if (errorsRange) {
    buttons.push({ text: 'Go to errors', onClick: async () => await selectRange(errorsRange) });
  }
  if (logRange) {
    buttons.push({ text: 'Go to log', onClick: async () => await selectRange(logRange) });
  }

  requestStore.showPane({ buttons, ...params });
};

const ensureWorksheetAssociation = async (
  worksheets: Excel.Worksheet[],
  {
    worksheetIdGetter,
    updateWorksheetId,
  }: {
    worksheetIdGetter: () => string;
    updateWorksheetId: (id: string) => Promise<void>;
  },
): Promise<boolean> => {
  const worksheetId = worksheetIdGetter();
  if (!worksheets.find(({ id }) => id === worksheetId)) {
    const dialogMessage =
      'This flow step cannot be run because it’s not associated with any worksheet. Would you like to associate it with the active sheet (you may need to revisit mappings)';
    const key = await requestStore.showDialog(dialogMessage, Data.flowstepButtons);
    if (key === Buttons.Cancel.key) {
      return false;
    } else {
      const activeWorksheet = await office.getActiveWorksheet();
      await updateWorksheetId(activeWorksheet.id);
    }
  }
  return true;
};

const createV2Logger = async (flowstep: Flowstep, meta: Record<string, any>, parentRunnable: any) => {
  const { logger: globalLogger, context } = meta;
  const worksheet = context.workbook.worksheets.getItem(flowstep.options.worksheetId);
  worksheet.load('name');
  await context.sync();
  return new InstanceLogger(
    globalLogger,
    {
      worksheetName: worksheet.name,
      name: `Flow step ${flowstep.name}`,
      borderFormat: V1FlowstepData.BorderFormat,
      userName: userStore.userName,
    },
    parentRunnable?.logger,
  );
};

const addFlowstepEvents = (executorLogger: InstanceLogger, flowstep: Flowstep) => {
  appEventBus.addListener(ExecutorEvents.ActionStarted, () => {
    executorLogger.info(`Execution started`);
  });
  appEventBus.addListener(ExecutorEvents.ActionInfo, (event: ExecutorActionInfo) => {
    executorLogger.info(event.message);
  });
  appEventBus.addListener(ExecutorEvents.ActionEvent, (event: ExecutorActionEvent) => {
    requestStore.setText(event.message, { description: event.description, withSpinner: true });
  });

  appEventBus.addListener(ExecutorEvents.ActionFinished, (event: ExecutorActionFinished) => {
    const { warnings } = event.result;
    if (warnings?.length) {
      const firstWarning = warnings[0];
      if (firstWarning instanceof NoDataError) {
        const { message } = firstWarning;
        const logErrorRange = executorLogger.info(message);
        showPane({ message, type: Type.Info }, logErrorRange);
      }
      return;
    }

    const { resultSuccessRange } = event.result;
    resultSuccessRange?.track();

    showPane({
      message: `Flow step '${flowstep.name}' was successfully executed`,
      buttons: resultSuccessRange
        ? [
            {
              text: `Show ${flowstep.operation === Operation.PULL ? 'data' : 'results'}`,
              onClick: async () => await selectRange(resultSuccessRange),
            },
          ]
        : [],
      type: Type.Success,
      timeout: Data.messagePaneTimeout,
    });

    executorLogger.info(V1FlowstepData.successText);
  });

  appEventBus.addListener(ExecutorEvents.ActionError, (event: ExecutorActionError) => {
    const { error } = event;

    let logErrorRange = null;
    let errorsRange = null;

    if (error instanceof PushError) {
      const conversionErrors = error.convertionError?.errors ?? [];
      const resultErrors = error.resultError?.errors ?? [];

      const totalLimit = Data.maxErrorsToDisplay;
      const conversionLimit = Math.max(totalLimit / 2, totalLimit - resultErrors.length);
      const resultLimit = Math.max(totalLimit / 2, totalLimit - conversionErrors.length);

      const trimmedConversionErrors = conversionErrors.slice(0, conversionLimit);
      const trimmedResultErrors = resultErrors.slice(0, resultLimit);

      const allErrors = [...conversionErrors, ...resultErrors];

      logErrorRange = executorLogger.error(allErrors);

      error.message = 'Errors were encountered';

      if (error.resultError) {
        errorsRange = error.resultError.range;
      }

      let errorSummary = '';
      if (trimmedConversionErrors.length > 0) {
        errorSummary += `\nConversion errors (${trimmedConversionErrors.length} of ${conversionErrors.length} shown):\n`;
        errorSummary += trimmedConversionErrors.map((x: string, index: number) => `${index + 1}. ${x}`).join('\n');
      }

      if (trimmedResultErrors.length > 0) {
        if (errorSummary) errorSummary += '\n';
        errorSummary += `\nResult errors (${trimmedResultErrors.length} of ${resultErrors.length} shown):\n`;
        errorSummary += trimmedResultErrors.map((x: string, index: number) => `${index + 1}. ${x}`).join('\n');
      }

      error.message = `${error.message}\n${errorSummary}`;
    } else if (error instanceof ConstantsConversionsError) {
      logErrorRange = executorLogger.error(error.errors);

      error.message = 'Errors were encountered during constants conversion';
      const trimmed = error.errors.slice(0, Data.maxErrorsToDisplay);
      error.message += `\n(${trimmed.length} of ${error.errors.length} shown):\n`;
      error.message += trimmed.map((x: string, index: number) => `${index + 1}. ${x}`).join('\n');
    } else {
      logErrorRange = executorLogger.error(error.message);
    }

    executorLogger.error(`Unable to execute flow step`);

    showPane({ message: `${flowstep.name} - ${error.message}`, type: Type.Error }, logErrorRange, errorsRange);
  });

  appEventBus.addListener(ExecutorEvents.ActionCancelled, () => {
    executorLogger.warn(`Execution was terminated by user`);
    requestStore.warn(`Execution of flow step '${flowstep.name}' was terminated by user`, Data.messagePaneTimeout);
  });

  appEventBus.addListener(ExecutorEvents.NewWorksheetCreated, (event: ExecutorNewWorksheetCreated) => {
    executorLogger.setLogParams({ worksheetName: event.sheetName });
  });

  return () => {
    appEventBus.clearListeners(ExecutorEvents.ActionStarted);
    appEventBus.clearListeners(ExecutorEvents.ActionInfo);
    appEventBus.clearListeners(ExecutorEvents.ActionEvent);
    appEventBus.clearListeners(ExecutorEvents.ActionFinished);
    appEventBus.clearListeners(ExecutorEvents.ActionError);
    appEventBus.clearListeners(ExecutorEvents.ActionCancelled);
    appEventBus.clearListeners(ExecutorEvents.NewWorksheetCreated);
    executorLogger.close();
  };
};

const runV2Executor = async (flowstep: Flowstep, meta: any, parentRunnable: any) => {
  const { cancelToken } = meta;

  const executor = executorFactory.getExecutor(flowstep, { workbookContext: meta.context, timezone: meta.timezone });
  const executorLogger = await createV2Logger(flowstep, meta, parentRunnable);

  const clearEvents = addFlowstepEvents(executorLogger, flowstep);

  const result = await executor.execute(cancelToken);
  clearEvents();
  return result;
};

export const execute = async (flowsteps: MigrationFlowstep[], meta: any, parentRunnable: any) => {
  const worksheetsToReload = new Set<string>();
  const worksheets = (await office.getAllWorksheets()) as Excel.Worksheet[];

  const result: { flowsteps: { result: any }[]; stopReason: StopReason } = {
    flowsteps: [],
    stopReason: StopReason.Normal,
  };

  for (let i = 0; i < flowsteps.length; ++i) {
    const flowstep = flowsteps[i];

    const isV2 = isNewFlowstepFormat(flowstep);
    const worksheetIdGetter = isV2 ? () => flowstep.originalFlowstep.options.worksheetId : () => flowstep.worksheetId;

    const updateWorksheetId = async (worksheetId: string) => {
      if (isV2) {
        flowstep.originalFlowstep.options.worksheetId = worksheetId;
        await flowstepStorage.saveFlowstep(flowstep.originalFlowstep);
      } else {
        flowstep.worksheetId = worksheetId;
        await dataStore.createOrUpdateFlowstep(flowstep);
      }
    };

    if (!(await ensureWorksheetAssociation(worksheets, { worksheetIdGetter, updateWorksheetId }))) {
      result.stopReason = StopReason.User;
      continue;
    }

    let executionResult;
    if (isV2) {
      executionResult = await runV2Executor(
        isObservable(flowstep.originalFlowstep) ? toJS(flowstep.originalFlowstep) : flowstep.originalFlowstep,
        meta,
        parentRunnable,
      );
    } else {
      const executor = createExecutor(flowstep, meta, parentRunnable);
      executionResult = await executor.run();
    }

    if (isV2 ? flowstep.originalFlowstep.options.recalculateFormulas : flowstep.options.recalculateFormulas) {
      await office.calculateFormulas();
    }

    result.flowsteps.push({ result: executionResult });
    worksheetsToReload.add(flowstep.options.worksheetId);

    const { error, warnings } = executionResult;
    const hasDataErrors = warnings?.some((x: Error) => x instanceof ResultDataError);

    if (!error && !hasDataErrors) {
      continue;
    }
    if (error && error instanceof CancelError) {
      throw error;
    }

    worksheetsToReload.delete(flowstep.options.worksheetId);

    const reaction = isV2 ? flowstep.errorReaction : flowstep.options.errorReaction;
    if (reaction === ErrorReaction.Continue) {
      continue;
    }
    if (reaction === ErrorReaction.Stop) {
      result.stopReason = StopReason.ErrorReaction;
      break;
    }

    // ErrorReaction.AskUser
    if (i < flowsteps.length - 1) {
      const dialogMessage = `Error happened while executing flow step '${flowstep.name}'. Do you want to ignore it and continue execution?`;
      const key = await requestStore.showDialog(dialogMessage, Data.buttons);
      if (key === Buttons.Stop.key) {
        result.stopReason = StopReason.User;
        break;
      }
    } else {
      result.stopReason = StopReason.ErrorReaction;
      break;
    }
  }

  requestStore.setText('Reloading context', { withSpinner: true });
  await contextStore.reloadWorksheetContext([...worksheetsToReload]);

  return result;
};
